import type {
  AdminDashboardStep,
  AdminUnpaidInvoiceRow,
} from "@/admin/entities/adminDashboard";

const LOCALE = "ru-RU";
const MONTHS_SHORT = [
  "янв",
  "фев",
  "мар",
  "апр",
  "май",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек",
];

const round1 = (value: number) => Math.round(value * 10) / 10;

/** Крупное значение карточки и ячейки таблицы: целые доллары. */
export function formatUsd(value: number): string {
  const rounded = Math.round(value) || 0;
  return `${rounded.toLocaleString(LOCALE)} USD`;
}

/** Подсказка графика: с центами. */
export function formatUsdPrecise(value: number): string {
  return `${value.toLocaleString(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} USD`;
}

/** Подписи оси: тысячи и миллионы. */
export function formatUsdShort(value: number): string {
  const abs = Math.abs(value);
  const format = (scaled: number) =>
    scaled.toLocaleString(LOCALE, { maximumFractionDigits: 1 });
  if (abs >= 1_000_000) return `${format(value / 1_000_000)} млн $`;
  if (abs >= 1_000) return `${format(value / 1_000)} тыс. $`;
  return `${format(value)} $`;
}

export function formatCount(value: number): string {
  return value.toLocaleString(LOCALE);
}

export function formatPercent(value: number): string {
  // Округляем до десятых заранее и гасим -0, иначе -0,01 печатается как «-0%».
  const rounded = round1(value) || 0;
  return `${rounded.toLocaleString(LOCALE, { maximumFractionDigits: 1 })}%`;
}

/** День по UTC: биллинг и дашборд живут днями UTC. */
export function formatDay(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${date.getUTCFullYear()}`;
}

/** Изменение к прошлому периоду в процентах; без базы тренда нет. */
export function computeTrend(
  current: number,
  previous: number | null | undefined
): number | null {
  if (previous === null || previous === undefined || previous === 0) {
    return null;
  }
  return round1(((current - previous) / Math.abs(previous)) * 100);
}

export function formatTrend(trend: number): string {
  const rounded = round1(trend) || 0;
  const sign = rounded > 0 ? "+" : "";
  return `${sign}${rounded.toLocaleString(LOCALE, { maximumFractionDigits: 1 })}%`;
}

export type TrendTone = "success" | "error" | "neutral";

export type TrendPolarity = "higherIsBetter" | "higherIsWorse";

/** Цвет тренда по смыслу: рост трат красный, рост продаж зелёный. */
export function trendTone(
  trend: number | null,
  polarity: TrendPolarity
): TrendTone {
  if (trend === null || trend === 0) return "neutral";
  const good = polarity === "higherIsBetter" ? trend > 0 : trend < 0;
  return good ? "success" : "error";
}

export function pluralRu(
  count: number,
  forms: [string, string, string]
): string {
  const lastTwo = Math.abs(count) % 100;
  const last = lastTwo % 10;
  if (lastTwo > 10 && lastTwo < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

export function revenueSharePercent(
  revenueShareUSD: number,
  salesUSD: number
): number | null {
  if (salesUSD <= 0) return null;
  return round1((revenueShareUSD / salesUSD) * 100);
}

export function formatBucketLabel(
  date: string,
  step: AdminDashboardStep
): string {
  const value = new Date(date);
  const day = String(value.getUTCDate()).padStart(2, "0");
  const month = String(value.getUTCMonth() + 1).padStart(2, "0");
  if (step === "MONTH") {
    return `${MONTHS_SHORT[value.getUTCMonth()]} ${value.getUTCFullYear()}`;
  }
  if (step === "WEEK") return `с ${day}.${month}`;
  return `${day}.${month}`;
}

// Подпись просрочки общая с карточкой платежа в биллинге.
export { overdueLabel } from "@/shared/utils/overdueDays";

export interface UnpaidSummary {
  totalUSD: number;
  count: number;
  overdueUSD: number;
  overdueCount: number;
  /** Счета без курса к USD: в count входят, в суммы нет. */
  withoutRateCount: number;
}

/** Карточка «Ждём оплаты» из того же списка, что таблица неоплаченных. */
export function summarizeUnpaid(
  rows: Pick<AdminUnpaidInvoiceRow, "amountUSD" | "overdueDays">[]
): UnpaidSummary {
  const money = (value: number) => Math.round(value * 100) / 100;
  const overdue = rows.filter((row) => row.overdueDays > 0);
  return {
    totalUSD: money(
      rows.reduce((total, row) => total + (row.amountUSD ?? 0), 0)
    ),
    count: rows.length,
    overdueUSD: money(
      overdue.reduce((total, row) => total + (row.amountUSD ?? 0), 0)
    ),
    overdueCount: overdue.length,
    withoutRateCount: rows.filter((row) => row.amountUSD === null).length,
  };
}
